import {useTranslation} from 'react-i18next';
import {Checkbox, FormControlLabel, Stack, TextField, Typography} from '@mui/material';
import {compileVolatileRules} from '_logics/pghistoryVolatileRules';
import LFCButton from '_components/inputs/LFCButton';
import {
  VOLATILE_MAX_ITEMS,
  VOLATILE_MAX_PATTERN,
  VOLATILE_MAX_REPLACEMENT,
  VolatilePattern
} from '_logics/LFCPgHistoryAccumUtil';

type Props = {
  items: VolatilePattern[];
  disabled: boolean;
  error: boolean;
  onChange: (items: VolatilePattern[]) => void;
};

/** git 行の揮発値の正規化ルール（変更判定のハッシュ計算前に各行へ適用する置換）の編集欄 */
export default function VolatilePatternsEditor({items, disabled, error, onChange}: Props) {
  const {t} = useTranslation();
  const edit = (index: number, patch: Partial<VolatilePattern>) =>
    onChange(items.map((item, i) => (i === index ? {...item, ...patch} : item)));
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{t('accum.volatile_patterns')}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t('accum.volatile_patterns_help')}
      </Typography>
      {items.map((item, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            label={t('accum.volatile_pattern')}
            value={item.pattern}
            disabled={disabled}
            error={error && (!item.pattern || item.pattern.length > VOLATILE_MAX_PATTERN)}
            helperText={
              item.pattern && compileVolatileRules([item]) === null
                ? t('accum.volatile_js_incompatible')
                : undefined
            }
            FormHelperTextProps={{sx: {color: 'warning.main'}}}
            onChange={e => edit(index, {pattern: e.target.value})}
          />
          <TextField
            size="small"
            fullWidth
            label={t('accum.volatile_replacement')}
            value={item.replacement}
            disabled={disabled || !!item.drop}
            error={error && item.replacement.length > VOLATILE_MAX_REPLACEMENT}
            onChange={e => edit(index, {replacement: e.target.value})}
          />
          <FormControlLabel
            sx={{whiteSpace: 'nowrap', mr: 0}}
            label={t('accum.volatile_drop')}
            control={
              <Checkbox
                size="small"
                checked={!!item.drop}
                disabled={disabled}
                onChange={e =>
                  edit(index, e.target.checked ? {drop: true, replacement: ''} : {drop: false})
                }
              />
            }
          />
          <LFCButton
            color="secondary"
            disabled={disabled}
            onClick={() => onChange(items.filter((_, i) => i !== index))}
          >
            {t('accum.volatile_remove')}
          </LFCButton>
        </Stack>
      ))}
      <div>
        <LFCButton
          color="secondary"
          disabled={disabled || items.length >= VOLATILE_MAX_ITEMS}
          onClick={() => onChange([...items, {pattern: '', replacement: ''}])}
        >
          {t('accum.volatile_add')}
        </LFCButton>
      </div>
    </Stack>
  );
}
